import React, { useEffect, useRef, useState } from 'react';
import getAllBlogPosts from './Blogs.js';
import PROJECTS from './projects.js';
import './App.css';

const WORK_HIGHLIGHT_PROJECTS = PROJECTS.filter(project => project.slug !== 'artemis-agent-optimization');

const SITE_URL = 'https://manaladham.com';
const DEFAULT_SOCIAL_IMAGE = `${SITE_URL}/social-card.png`;
const GA_MEASUREMENT_ID = 'G-S2KZS4S03V';
const ANALYTICS_CONSENT_KEY = 'manal-adham-analytics-consent';
const NEWSLETTER_URL = 'https://substack.com/@manaladham';
const NEWSLETTER_CTA_LABEL = 'Follow on Substack';
const SGN_SUBSTACK_URL = 'https://substack.com/@saudiglobalnetwork';
const BLOG_POSTS = getAllBlogPosts();
const PATH_TO_SECTION = {
  '': 'home',
  about: 'about',
  consulting: 'consulting',
  work: 'projects',
  projects: 'projects',
  blog: 'blog',
  talks: 'talks',
  research: 'conferences',
  publications: 'conferences',
  contact: 'contact'
};
const SECTION_TO_PATH = {
  home: '',
  about: 'about',
  consulting: 'consulting',
  projects: 'projects',
  blog: 'blog',
  talks: 'talks',
  conferences: 'publications',
  contact: 'contact'
};

const getUrlState = () => {
  const { pathname, search } = window.location;
  const params = new URLSearchParams(search);
  const segments = pathname.replace(/\/+$/, '').split('/').filter(Boolean);

  // Prefer clean paths: /blog/slug, /projects/slug, /consulting
  if (segments[0] === 'blog' && segments[1]) {
    const article = BLOG_POSTS.find(post => post.slug === segments[1]);
    if (article) {
      return {
        activeSection: 'blog',
        selectedArticle: article.id,
        selectedCaseStudy: null
      };
    }
  }

  if ((segments[0] === 'work' || segments[0] === 'projects') && segments[1]) {
    const caseStudy = PROJECTS.find(project => project.slug === segments[1] && project.caseStudy);
    if (caseStudy) {
      return {
        activeSection: 'projects',
        selectedArticle: null,
        selectedCaseStudy: caseStudy.slug
      };
    }
  }

  if (segments[0] && PATH_TO_SECTION[segments[0]]) {
    return {
      activeSection: PATH_TO_SECTION[segments[0]],
      selectedArticle: null,
      selectedCaseStudy: null
    };
  }

  // Legacy query URLs still resolve, then get rewritten to clean paths.
  const article = BLOG_POSTS.find(post => post.slug === params.get('article'));
  if (article) {
    return {
      activeSection: 'blog',
      selectedArticle: article.id,
      selectedCaseStudy: null
    };
  }

  const caseStudy = PROJECTS.find(project => project.slug === params.get('case') && project.caseStudy);
  if (caseStudy) {
    return {
      activeSection: 'projects',
      selectedArticle: null,
      selectedCaseStudy: caseStudy.slug
    };
  }

  const sectionParam = params.get('section');
  if (sectionParam && PATH_TO_SECTION[sectionParam]) {
    return {
      activeSection: PATH_TO_SECTION[sectionParam],
      selectedArticle: null,
      selectedCaseStudy: null
    };
  }

  return {
    activeSection: 'home',
    selectedArticle: null,
    selectedCaseStudy: null
  };
};

const getSectionUrl = (section) => {
  const path = SECTION_TO_PATH[section];
  return path ? `/${path}` : '/';
};

const getCaseStudyUrl = (slug) => `/projects/${slug}`;
const getArticleUrl = (slug) => `/blog/${slug}`;

const getCanonicalPath = ({ activeSection, selectedArticle, selectedCaseStudy }) => {
  if (selectedArticle) {
    const post = BLOG_POSTS.find(item => item.id === selectedArticle);
    if (post) return getArticleUrl(post.slug);
  }
  if (selectedCaseStudy) return getCaseStudyUrl(selectedCaseStudy);
  return getSectionUrl(activeSection);
};

const getStoredAnalyticsConsent = () => {
  try {
    const storedConsent = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return storedConsent === 'granted' || storedConsent === 'denied'
      ? storedConsent
      : 'unset';
  } catch {
    return 'unset';
  }
};

const loadGoogleAnalytics = () => {
  if (document.getElementById('google-analytics-script')) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied'
  });

  const script = document.createElement('script');
  script.id = 'google-analytics-script';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { send_page_view: false });
};

const clearGoogleAnalyticsCookies = () => {
  document.cookie.split(';').forEach(cookie => {
    const cookieName = cookie.split('=')[0].trim();
    if (!cookieName.startsWith('_ga')) return;

    document.cookie = `${cookieName}=; Max-Age=0; path=/; SameSite=Lax`;
    document.cookie = `${cookieName}=; Max-Age=0; path=/; domain=.manaladham.com; SameSite=Lax`;
  });
};

const PUBLICATIONS = [
  {
    title: "Evolving Excellence: Automated Optimization of LLM-based Agents",
    venue: "arXiv preprint",
    year: "2025",
    logo: "https://images.unsplash.com/photo-1751887687443-ec4dc66e230d?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://arxiv.org/abs/2512.09108"
  },
  {
    title: "evoML Yellow Paper: Evolutionary AI and Optimisation Studio",
    venue: "arXiv preprint",
    year: "2022",
    logo: "https://images.unsplash.com/photo-1774901128215-3549cc686921?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://arxiv.org/abs/2212.10671"
  },
  {
    title: "Artificial Ecosystem Algorithm Applied to Multi-Line Steel Scheduling",
    venue: "IEEE Congress on Evolutionary Computation (CEC)",
    year: "2019",
    pages: "982–989",
    logo: "https://images.unsplash.com/photo-1760378105099-968c06b9b4bd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1109/cec.2019.8790320"
  },
  {
    title: "Evaluating Decomposition Strategies to Enable Scalable Scheduling for a Real-world Multi-line Steel Scheduling Problem",
    venue: "IEEE Symposium Series on Computational Intelligence (SSCI)",
    year: "2017",
    pages: "1–8",
    logo: "https://images.unsplash.com/photo-1760378105099-968c06b9b4bd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1109/ssci.2017.8285185"
  },
  {
    title: "Evaluating Clustering Methods within the Artificial Ecosystem Algorithm and their Application to Bike Redistribution in London",
    venue: "Biosystems (Elsevier)",
    year: "2016",
    pages: "43–59",
    logo: "https://images.unsplash.com/photo-1765988804945-87038fae96dd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1016/j.biosystems.2016.04.008"
  },
  {
    title: "An Ecosystem Algorithm for the Dynamic Redistribution of Bicycles in London",
    venue: "International Conference on Information Processing in Cells and Tissues",
    year: "2015",
    pages: "39–51",
    logo: "https://images.unsplash.com/photo-1765988804945-87038fae96dd?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1007/978-3-319-23108-2_4"
  },
  {
    title: "An Artificial Ecosystem Algorithm Applied to Static and Dynamic Travelling Salesman Problems",
    venue: "IEEE Symposium Series on Computational Intelligence (SSCI)",
    year: "2014",
    pages: "149–156",
    logo: "https://images.unsplash.com/photo-1742415105826-0d588fa879b9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1109/ices.2014.7008734"
  },
  {
    title: "An Artificial Ecosystem Algorithm Applied to the Travelling Salesman Problem",
    venue: "Proceedings of the 2014 Annual Conference on Genetic and Evolutionary Computation (GECCO)",
    year: "2014",
    pages: "155–156",
    logo: "https://images.unsplash.com/photo-1742415105826-0d588fa879b9?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1145/2598394.2598438"
  },
  {
    title: "How to attack two-factor authentication internet banking",
    venue: "International Conference on Financial Cryptography and Data Security",
    year: "2013",
    pages: "322–328",
    logo: "https://images.unsplash.com/photo-1614064641938-3bbee52942c7?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80",
    link: "https://doi.org/10.1007/978-3-642-39884-1_27"
  }
];

const PUB_LOGO_FALLBACKS = ['📄', '📝', '📚', '🧪', '🔬', '✨'];

const PubLogo = ({ pub, index }) => {
  const [errored, setErrored] = useState(false);
  const showImage = Boolean(pub.logo) && !errored;

  return (
    <div className="pub-row__logo">
      {showImage ? (
        <img
          className="pub-row__logo-img"
          src={pub.logo}
          alt=""
          loading="lazy"
          onError={() => setErrored(true)}
        />
      ) : (
        <span className="pub-row__logo-fallback" aria-hidden="true">
          {PUB_LOGO_FALLBACKS[index % PUB_LOGO_FALLBACKS.length]}
        </span>
      )}
    </div>
  );
};

const App = () => {
  const initialUrlState = getUrlState();
  const [activeSection, setActiveSection] = useState(initialUrlState.activeSection);
  const [selectedArticle, setSelectedArticle] = useState(initialUrlState.selectedArticle);
  const [selectedCaseStudy, setSelectedCaseStudy] = useState(initialUrlState.selectedCaseStudy);
  const [analyticsConsent, setAnalyticsConsent] = useState(getStoredAnalyticsConsent);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const lastTrackedPage = useRef('');

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    intent: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');

    try {
      const response = await fetch('https://formspree.io/f/mzzadban', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactForm.name,
          email: contactForm.email,
          intent: contactForm.intent,
          message: contactForm.message
        })
      });

      if (response.ok) {
        setFormStatus('success');
        setContactForm({ name: '', email: '', intent: '', message: '' });
        window.gtag?.('event', 'generate_lead', {
          form_name: 'contact',
          page_location: window.location.href
        });
        setTimeout(() => setFormStatus(''), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus(''), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus(''), 3000);
    }
  };

  const handleContactChange = (e) => {
    setContactForm({
      ...contactForm,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    const handlePopState = () => {
      const nextState = getUrlState();
      setActiveSection(nextState.activeSection);
      setSelectedArticle(nextState.selectedArticle);
      setSelectedCaseStudy(nextState.selectedCaseStudy);
      window.scrollTo({ top: 0 });
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const selectedPost = BLOG_POSTS.find(post => post.id === selectedArticle);
    const selectedProject = PROJECTS.find(project => project.slug === selectedCaseStudy);
    const pageDetails = {
      home: {
        title: 'Manal Adham | AI Research Engineer',
        description: 'Manal Adham is a UCL AI PhD and AI research engineer based between London and Riyadh, working across optimisation, machine learning, and AI.'
      },
      about: {
        title: 'What I Do | Manal Adham',
        description: 'What Manal Adham does day to day — research, building, consulting, speaking, writing, and ecosystem building.'
      },
      consulting: {
        title: 'Collaboration | Manal Adham',
        description: 'Collaborate with Manal Adham on applied AI, research partnerships, technical due diligence, and speaking.'
      },
      projects: {
        title: 'Portfolio | Manal Adham',
        description: 'Explore Manal Adham’s applied AI and product work across enterprise search, optimisation, property technology, and UK and Saudi business networks.'
      },
      blog: {
        title: 'Blog | Manal Adham',
        description: 'Practical articles by Manal Adham on production AI, enterprise agents, machine learning evaluation, financial segmentation, and synthetic data.'
      },
      talks: {
        title: 'AI Keynote Speaker & Public Speaking | Manal Adham',
        description: 'Book Manal Adham for keynotes, panels, and presentations on AI innovation, entrepreneurship, research, and UK and Saudi collaboration.'
      },
      conferences: {
        title: 'Publications | Manal Adham',
        description: 'Peer-reviewed research by Manal Adham on evolutionary computing, artificial ecosystem algorithms, optimisation, logistics, and applied AI.'
      },
      contact: {
        title: 'Contact Manal Adham | AI Collaboration',
        description: 'Contact Manal Adham about AI innovation, research, speaking, investment, and collaboration opportunities in the UK and Saudi Arabia.'
      }
    };

    const details = selectedPost
      ? {
          title: `${selectedPost.title} | Manal Adham`,
          description: selectedPost.excerpt
        }
      : selectedProject
        ? {
            title: `${selectedProject.title} Case Study | Manal Adham`,
            description: selectedProject.caseStudy.excerpt
          }
        : pageDetails[activeSection] || pageDetails.home;
    const canonicalPath = getCanonicalPath({
      activeSection,
      selectedArticle,
      selectedCaseStudy
    });
    const canonicalUrl = `${SITE_URL}${canonicalPath}`;

    // Keep shared and bookmarked query URLs pointing at clean paths.
    const normalizedPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const currentPath = `${normalizedPath}${window.location.search}`;
    if (currentPath !== canonicalPath) {
      window.history.replaceState({}, '', canonicalPath);
    }

    document.title = details.title;
    document.documentElement.lang = 'en-GB';

    const setMeta = (selector, attribute, value, identityAttribute, identityValue) => {
      let element = document.head.querySelector(selector);
      if (!element && identityAttribute && identityValue) {
        element = document.createElement('meta');
        element.setAttribute(identityAttribute, identityValue);
        document.head.appendChild(element);
      }
      if (element) element.setAttribute(attribute, value);
    };

    setMeta('meta[name="description"]', 'content', details.description);
    setMeta('meta[name="title"]', 'content', details.title);
    setMeta('meta[property="og:title"]', 'content', details.title);
    setMeta('meta[property="og:description"]', 'content', details.description);
    setMeta('meta[property="og:url"]', 'content', canonicalUrl);
    const openGraphType = selectedPost || selectedProject
      ? 'article'
      : activeSection === 'home'
        ? 'profile'
        : 'website';
    setMeta('meta[property="og:type"]', 'content', openGraphType);
    setMeta('meta[name="twitter:title"]', 'content', details.title);
    setMeta('meta[name="twitter:description"]', 'content', details.description);
    setMeta('meta[name="twitter:url"]', 'content', canonicalUrl);
    setMeta('meta[name="twitter:card"]', 'content', 'summary_large_image');
    setMeta('link[rel="canonical"]', 'href', canonicalUrl);

    // Prefer the first-party social card for shares — Unsplash crops are too small for OG.
    const socialImage = DEFAULT_SOCIAL_IMAGE;
    const socialImageAlt = selectedPost
      ? `${selectedPost.title} — article by Manal Adham`
      : selectedProject
        ? `${selectedProject.title} — case study by Manal Adham`
        : 'Manal Adham, AI research engineer based between London and Riyadh';
    [
      { selector: 'meta[property="og:image"]', key: 'property', value: 'og:image', content: socialImage },
      { selector: 'meta[property="og:image:secure_url"]', key: 'property', value: 'og:image:secure_url', content: socialImage },
      { selector: 'meta[property="og:image:alt"]', key: 'property', value: 'og:image:alt', content: socialImageAlt },
      { selector: 'meta[name="twitter:image"]', key: 'name', value: 'twitter:image', content: socialImage },
      { selector: 'meta[name="twitter:image:alt"]', key: 'name', value: 'twitter:image:alt', content: socialImageAlt }
    ].forEach(({ selector, key, value, content }) => {
      setMeta(selector, 'content', content, key, value);
    });

    const fixedImageMeta = [
      ['og:image:type', 'image/png'],
      ['og:image:width', '1200'],
      ['og:image:height', '630']
    ];
    fixedImageMeta.forEach(([property, content]) => {
      setMeta(`meta[property="${property}"]`, 'content', content, 'property', property);
    });

    const articleMeta = [
      ['article:published_time', selectedPost?.dateISO || selectedProject?.caseStudy.datePublished],
      ['article:modified_time', selectedPost?.dateModifiedISO || selectedPost?.dateISO || selectedProject?.caseStudy.dateModified],
      ['article:author', selectedPost || selectedProject ? `${SITE_URL}/#person` : null],
      ['article:section', selectedPost?.category || (selectedProject ? 'Applied AI Case Study' : null)]
    ];
    articleMeta.forEach(([property, content]) => {
      const selector = `meta[property="${property}"]`;
      if (content) {
        setMeta(selector, 'content', content, 'property', property);
      } else {
        document.head.querySelector(selector)?.remove();
      }
    });

    document.getElementById('dynamic-page-schema')?.remove();

    const personReference = {
      '@type': 'Person',
      '@id': `${SITE_URL}/#person`,
      name: 'Manal Adham',
      url: `${SITE_URL}/`
    };
    const sectionNames = {
      about: 'What I Do',
      consulting: 'Collaboration',
      projects: 'Portfolio',
      blog: 'Blog',
      talks: 'Speaking',
      conferences: 'Publications',
      contact: 'Contact'
    };
    const primarySchema = selectedPost
      ? {
          '@type': 'BlogPosting',
          '@id': `${canonicalUrl}#article`,
          url: canonicalUrl,
          headline: selectedPost.title,
          description: selectedPost.excerpt,
          image: [DEFAULT_SOCIAL_IMAGE, selectedPost.image].filter(Boolean),
          datePublished: selectedPost.dateISO,
          dateModified: selectedPost.dateModifiedISO || selectedPost.dateISO,
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': canonicalUrl
          },
          articleSection: selectedPost.category,
          keywords: selectedPost.category,
          inLanguage: 'en-GB',
          author: personReference,
          publisher: personReference,
          isPartOf: {
            '@id': `${SITE_URL}/#website`
          }
        }
      : selectedProject
        ? {
            '@type': 'Article',
            '@id': `${canonicalUrl}#case-study`,
            url: canonicalUrl,
            headline: selectedProject.title,
            description: selectedProject.caseStudy.excerpt,
            image: [DEFAULT_SOCIAL_IMAGE, selectedProject.image].filter(Boolean),
            datePublished: selectedProject.caseStudy.datePublished,
            dateModified: selectedProject.caseStudy.dateModified,
            articleSection: 'Applied AI Case Study',
            keywords: selectedProject.tech,
            inLanguage: 'en-GB',
            author: personReference,
            publisher: personReference,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': canonicalUrl
            },
            isPartOf: {
              '@id': `${SITE_URL}/#website`
            }
          }
        : {
            '@type': activeSection === 'home'
              ? 'ProfilePage'
              : ['blog', 'projects'].includes(activeSection)
                ? 'CollectionPage'
                : 'WebPage',
            '@id': `${canonicalUrl}#webpage`,
            url: canonicalUrl,
            name: details.title,
            description: details.description,
            inLanguage: 'en-GB',
            isPartOf: {
              '@id': `${SITE_URL}/#website`
            },
            about: {
              '@id': `${SITE_URL}/#person`
            },
            ...(activeSection === 'home' && {
              mainEntity: {
                '@id': `${SITE_URL}/#person`
              }
            }),
            ...(activeSection === 'blog' && {
              mainEntity: {
                '@type': 'ItemList',
                itemListElement: BLOG_POSTS.map((post, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  name: post.title,
                  url: `${SITE_URL}${getArticleUrl(post.slug)}`
                }))
              }
            }),
            ...(activeSection === 'projects' && {
              mainEntity: {
                '@type': 'ItemList',
                itemListElement: WORK_HIGHLIGHT_PROJECTS.map((project, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  name: project.title,
                  ...(project.slug && {
                    url: `${SITE_URL}${getCaseStudyUrl(project.slug)}`
                  })
                }))
              }
            }),
            ...(activeSection === 'consulting' && {
              mainEntity: {
                '@type': 'ProfessionalService',
                '@id': `${SITE_URL}/#consulting`,
                name: 'Manal Adham Research Collaboration',
                url: canonicalUrl,
                description: details.description,
                provider: personReference,
                areaServed: [
                  { '@type': 'Country', name: 'United Kingdom' },
                  { '@type': 'Country', name: 'Saudi Arabia' }
                ],
                serviceType: [
                  'Hands on AI development',
                  'AI strategy advisory',
                  'AI implementation assessment and optimisation',
                  'Technical due diligence',
                  'Research collaboration',
                  'Public speaking'
                ]
              }
            }),
            ...(activeSection === 'conferences' && {
              mainEntity: {
                '@type': 'ItemList',
                name: 'Published research by Manal Adham',
                itemListElement: PUBLICATIONS.map((pub, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@type': 'ScholarlyArticle',
                    name: pub.title,
                    url: pub.link,
                    datePublished: pub.year,
                    isPartOf: {
                      '@type': 'Periodical',
                      name: pub.venue
                    }
                  }
                }))
              }
            })
          };

    const schemaGraph = [primarySchema];
    if (activeSection !== 'home') {
      const sectionUrl = `${SITE_URL}${getSectionUrl(activeSection)}`;
      const breadcrumbItems = [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: `${SITE_URL}/`
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: sectionNames[activeSection] || details.title,
          item: sectionUrl
        }
      ];

      if (selectedPost || selectedProject) {
        breadcrumbItems.push({
          '@type': 'ListItem',
          position: 3,
          name: selectedPost?.title || selectedProject.title,
          item: canonicalUrl
        });
      }

      schemaGraph.push({
        '@type': 'BreadcrumbList',
        '@id': `${canonicalUrl}#breadcrumbs`,
        itemListElement: breadcrumbItems
      });
    }

    if (activeSection === 'talks' && !selectedPost && !selectedProject) {
      schemaGraph.push({
        '@type': 'Event',
        '@id': `${SITE_URL}/talks#3f-forum-2026`,
        name: 'Opening Central Asia to GCC Markets',
        description:
          'Keynote on opportunities for Central Asian entrepreneurs to enter Gulf Cooperation Council markets at the Female Founders Forum Tajikistan 2026.',
        startDate: '2026-06-01',
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        location: {
          '@type': 'Place',
          name: 'Dushanbe, Tajikistan',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Dushanbe',
            addressCountry: 'TJ'
          }
        },
        performer: personReference,
        organizer: {
          '@type': 'Organization',
          name: 'Female Founders Forum Tajikistan (3F Forum)'
        },
        image: `${SITE_URL}/talks/3f-forum-2026.png`,
        url: `${SITE_URL}/talks`
      });
    }

    const schema = document.createElement('script');
    schema.id = 'dynamic-page-schema';
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@graph': schemaGraph
    });
    document.head.appendChild(schema);
  }, [activeSection, selectedArticle, selectedCaseStudy]);

  useEffect(() => {
    if (analyticsConsent !== 'granted') return;

    loadGoogleAnalytics();
    window.gtag('consent', 'update', {
      analytics_storage: 'granted'
    });

    const pagePath = `${window.location.pathname}${window.location.search}`;
    if (lastTrackedPage.current === pagePath) return;

    lastTrackedPage.current = pagePath;
    window.gtag('event', 'page_view', {
      page_title: document.title,
      page_location: window.location.href,
      page_path: pagePath
    });
  }, [activeSection, selectedArticle, selectedCaseStudy, analyticsConsent]);

  const updateAnalyticsConsent = (consent) => {
    try {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, consent);
    } catch {
      // Analytics remains optional if browser storage is unavailable.
    }

    if (consent === 'denied' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      });
      clearGoogleAnalyticsCookies();
    }

    setAnalyticsConsent(consent);
  };

  const conferences = [
    {
      name: "IEEE Congress on Evolutionary Computation",
      year: "2019",
      location: "Wellington, New Zealand",
      role: "Presenter"
    },
    {
      name: "IEEE Symposium Series on Computational Intelligence",
      year: "2017",
      location: "Honolulu, Hawaii",
      role: "Presenter"
    },
    {
      name: "International Conference on Parallel Problem Solving from Nature",
      year: "2016",
      location: "Edinburgh, UK",
      role: "Presenter"
    },
    {
      name: "Genetic and Evolutionary Computation Conference",
      year: "2014",
      location: "Vancouver, Canada",
      role: "Presenter"
    }
  ];

  const keynotes = [
    {
      title: "Opening Central Asia to GCC Markets",
      event: "Female Founders Forum Tajikistan 2026 (3F Forum)",
      role: "Keynote",
      date: "June 2026",
      location: "Dushanbe, Tajikistan",
      description: "Keynote on opportunities for Central Asian entrepreneurs to enter Gulf Cooperation Council markets, delivered at the inaugural Female Founders Forum bringing together women founders, investors, and business leaders from across Central Asia and beyond.",
      image: `${process.env.PUBLIC_URL}/talks/3f-forum-2026.png`,
      imageAlt: "Manal Adham on stage at the Beyond Borders panel, Female Founders Forum 2026 in Dushanbe"
    }
  ];

  const firesideChats = [
    {
      title: "Energy Policy in Transition — Shaping the Future Landscape",
      guest: "Ahmed AlJameel",
      event: "Saudi Global Network — Climate Future",
      date: "February 2026",
      location: "London, UK",
      description: "Opening session of Saudi Global Network’s Climate Future programme, exploring how energy policy, market design, and geopolitics are reshaping the transition and unlocking scalable clean-energy growth between the UK and Saudi Arabia."
    },
    {
      title: "Innovation, Collaboration, and the Future of KSA–UK HealthTech and BioTech",
      guest: "Dr Sebastian Vaughan, CEO of Phytome Life Sciences",
      event: "Saudi Global Network",
      date: "November 2025",
      location: "London, UK",
      description: "Fireside conversation on biotechnology, AI-enabled plant-based therapeutics, and building high-performance UK–Saudi pathways in HealthTech and BioTech aligned with Vision 2030."
    },
    {
      title: "Saudi–UK Trade and Investment Opportunities",
      guest: "Abdullah AlMasoud, Saudi Commercial Attaché to the United Kingdom",
      event: "Saudi Global Network",
      date: "September 2025",
      location: "London, UK",
      description: "Discussion on Saudi Arabia’s evolving business landscape, Vision 2030 investment opportunities, and how founders, investors, and professionals can deepen UK–Saudi commercial collaboration."
    }
  ];

  const consultingOffers = [
    {
      title: "Collaborate With Me",
      description: "Hands on AI development, research partnerships, and joint ventures across industry and academia.",
      outcomes: [
        "Building production ready AI products",
        "Joint research and applied AI projects",
        "Industry and academia partnerships",
        "Prototypes taken to production"
      ]
    },
    {
      title: "Advise My Team",
      description: "Strategic guidance on applied AI, including how to improve systems you already have in production.",
      outcomes: [
        "AI strategy and roadmap reviews",
        "Assessing current AI implementations and optimising them",
        "Architecture and platform guidance",
        "Mentoring for data science teams"
      ]
    },
    {
      title: "Tech Investment Due Diligence",
      description: "Independent assessment of AI and technology ventures for investors, evaluating technical depth, product readiness, and risk.",
      outcomes: [
        "Technical depth assessment",
        "Product and team readiness review",
        "Clear risk and opportunity report"
      ]
    },
    {
      title: "Invite Me To Speak",
      description: "Keynotes, panels, and presentations on AI, entrepreneurship, and UK and Saudi collaboration.",
      outcomes: [
        "Keynotes and conference talks",
        "Panels and presentations",
        "Corporate and university sessions"
      ]
    }
  ];

  const workedWith = [
    { name: "Intel", logo: `${process.env.PUBLIC_URL}/logos/intel.svg` },
    { name: "Unilever", logo: `${process.env.PUBLIC_URL}/logos/unilever.svg` },
    { name: "BBC", logo: `${process.env.PUBLIC_URL}/logos/bbc.svg` },
    { name: "ArcelorMittal", logo: `${process.env.PUBLIC_URL}/logos/arcelormittal.svg` },
    { name: "Transport for London", logo: `${process.env.PUBLIC_URL}/logos/tfl.png` },
    { name: "Braintree", logo: `${process.env.PUBLIC_URL}/logos/braintree.svg` },
    { name: "University College London", logo: `${process.env.PUBLIC_URL}/logos/ucl.svg` },
    { name: "SAFFIR", logo: `${process.env.PUBLIC_URL}/logos/saffir.png` },
    { name: "TurinTech AI", logo: `${process.env.PUBLIC_URL}/logos/turintech.svg` },
    { name: "Bani & Al Culture", logo: `${process.env.PUBLIC_URL}/logos/bna-culture.png`, large: true }
  ];

  const consultingSteps = [
    {
      title: "Context",
      description: "Share a short brief: the problem, relevant links, and who else is involved."
    },
    {
      title: "Scope",
      description: "Agree on a clear scope, timeline, and expected outputs before work begins."
    },
    {
      title: "Delivery",
      description: "Focused work with regular check-ins, clear findings, and practical next steps."
    }
  ];

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'What I Do' },
    { id: 'projects', label: 'Portfolio' },
    { id: 'conferences', label: 'Publications' },
    { id: 'blog', label: 'Blog' },
    { id: 'talks', label: 'Speaking' },
    { id: 'contact', label: 'Contact' }
  ];

  const navigate = (section) => {
    window.history.pushState({}, '', getSectionUrl(section));
    setActiveSection(section);
    setSelectedArticle(null);
    setSelectedCaseStudy(null);
    setMobileNavOpen(false);
    window.scrollTo({ top: 0 });
  };

  const openArticle = (id) => {
    const article = BLOG_POSTS.find(post => post.id === id);
    if (!article) return;

    window.history.pushState({}, '', getArticleUrl(article.slug));
    window.scrollTo({ top: 0 });
    setActiveSection('blog');
    setSelectedArticle(id);
    setSelectedCaseStudy(null);
  };

  const closeArticle = () => {
    window.history.pushState({}, '', getSectionUrl('blog'));
    window.scrollTo({ top: 0 });
    setSelectedArticle(null);
  };

  const openCaseStudy = (slug) => {
    const project = PROJECTS.find(item => item.slug === slug && item.caseStudy);
    if (!project) return;

    window.history.pushState({}, '', getCaseStudyUrl(project.slug));
    window.scrollTo({ top: 0 });
    setActiveSection('projects');
    setSelectedCaseStudy(project.slug);
    setSelectedArticle(null);
  };

  const closeCaseStudy = () => {
    window.history.pushState({}, '', getSectionUrl('projects'));
    window.scrollTo({ top: 0 });
    setSelectedCaseStudy(null);
  };

  const renderHome = () => {
    return (
      <div>
        <section className="hero hero--home">
          <div className="container home-landing">
            <div className="home-landing__copy">
              <h1 className="hero__title">
                Manal Adham
              </h1>
              <p className="paper-byline">
                AI Research Engineer · Technology Entrepreneur · Ecosystem Builder
              </p>
              <div className="paper-abstract">
                <p>
                  I am an AI research engineer, technology entrepreneur, and ecosystem builder
                  working at the intersection of artificial intelligence, technology,
                  and global innovation.
                </p>
                <p>
                  I have a PhD in Artificial Intelligence from University College
                  London and over 15 years of experience across AI, machine learning,
                  and technology development. I have worked with organisations
                  including Intel, Unilever, and ArcelorMittal, helping turn advances
                  in AI research into practical technologies and real world
                  applications.
                </p>
                <p>
                  Alongside my work in AI, I am the founder of Saudi Global Network, a
                  platform connecting the Middle East and Europe through entrepreneurs,
                  investors, companies, and innovators. We form a rich connective tissue
                  at the bleeding edge of innovation, and our events give members a real
                  advantage: surrounded by inspiring people, in ways that make a genuine
                  difference to the direction of their careers.
                </p>
                <p>
                  My work brings together technology, research, business, and people,
                  with a focus on creating new ideas, building meaningful connections,
                  and turning opportunities into something tangible.
                </p>
              </div>
              <div className="hero__actions">
                <a
                  className="btn btn--ghost"
                  href={getSectionUrl('about')}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate('about');
                  }}
                >
                  What I Do
                </a>
                <a
                  className="btn btn--ghost"
                  href={getSectionUrl('conferences')}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate('conferences');
                  }}
                >
                  Publications
                </a>
                <a
                  className="btn btn--ghost"
                  href={getSectionUrl('projects')}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate('projects');
                  }}
                >
                  Portfolio
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="section section--tight section--marquee">
          <div className="container">
            <p className="eyebrow">Organisations I've worked with</p>
          </div>
          <div className="marquee" aria-label="Organisations Manal has worked with">
            <div className="marquee__track">
              {[...workedWith, ...workedWith].map((org, index) => (
                <span
                  key={index}
                  className="marquee__item"
                  aria-hidden={index >= workedWith.length}
                  title={org.name}
                >
                  {org.logo ? (
                    <img
                      className={`marquee__logo${org.large ? " marquee__logo--lg" : ""}`}
                      src={org.logo}
                      alt={org.name}
                      loading="lazy"
                    />
                  ) : (
                    <span className="marquee__name">{org.name}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  };

  const renderAbout = () => (
    <div>
      <section className="hero hero--compact">
        <div className="container">
          <p className="eyebrow">What I Do</p>
          <h1 className="statement">What I Do</h1>

          <div style={{ marginTop: '48px' }}>
            <div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Research</div>
                <div className="edit-row__body">
                  <p>
                    My core interest is applied AI research, including evolutionary
                    computing, optimisation, and more recently agentic AI systems. I'm a
                    Research Fellow at UCL, and the papers behind this work are on the{' '}
                    <a
                      href={getSectionUrl('conferences')}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('conferences');
                      }}
                    >
                      Publications
                    </a>{' '}
                    page.
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Building</div>
                <div className="edit-row__body">
                  <p>
                    I like turning research into things people actually use, from
                    enterprise search platforms to product prototypes. A selection is on
                    the{' '}
                    <a
                      href={getSectionUrl('projects')}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('projects');
                      }}
                    >
                      Portfolio
                    </a>{' '}
                    page.
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Consulting</div>
                <div className="edit-row__body">
                  <p>
                    I advise teams and investors on applied AI, including strategy,
                    hands-on development, and technical due diligence. More on the{' '}
                    <a
                      href={getSectionUrl('consulting')}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('consulting');
                      }}
                    >
                      Consulting
                    </a>{' '}
                    page.
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Due Diligence</div>
                <div className="edit-row__body">
                  <p>
                    I carry out technical due diligence on companies and business
                    opportunities for investors, assessing technical depth, product
                    readiness, and risk before capital is committed.
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Speaking</div>
                <div className="edit-row__body">
                  <p>
                    I enjoy speaking about AI research and entrepreneurship at
                    conferences, founder forums, and industry events. Keynotes and
                    panels are on the{' '}
                    <a
                      href={getSectionUrl('talks')}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('talks');
                      }}
                    >
                      Speaking
                    </a>{' '}
                    page.
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Writing</div>
                <div className="edit-row__body">
                  <p>
                    I write technical notes on production AI, agents, and evaluation on
                    the{' '}
                    <a
                      href={getSectionUrl('blog')}
                      onClick={(event) => {
                        event.preventDefault();
                        navigate('blog');
                      }}
                    >
                      Blog
                    </a>
                    .
                  </p>
                </div>
              </div>
              <div className="edit-row edit-row--compact">
                <div className="edit-row__label">Ecosystem Building</div>
                <div className="edit-row__body">
                  <p>
                    Build partnerships. Find investment. Access talent. Discover
                    opportunities. I founded{' '}
                    <a href="https://saudiglobalnetwork.org/" target="_blank" rel="noopener noreferrer">
                      Saudi Global Network
                    </a>{' '}
                    to facilitate business relationships between the Middle East and
                    Europe.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );

  const renderProjects = () => {
    if (selectedCaseStudy) {
      const project = PROJECTS.find(item => item.slug === selectedCaseStudy);
      if (!project?.caseStudy) return null;

      const { caseStudy } = project;

      return (
        <section className="hero hero--compact page-enter" key={`case-${project.slug}`}>
          <div className="container">
            <article className="article case-study">
              <a
                className="back-link"
                href={getSectionUrl('projects')}
                onClick={(event) => {
                  event.preventDefault();
                  closeCaseStudy();
                }}
              >
                ← Back to portfolio
              </a>
              <img
                className="article__hero"
                src={project.image}
                alt={`${project.title} case study`}
                fetchPriority="high"
              />
              <p className="eyebrow">Case study</p>
              <h1 className="article__title">{project.title}</h1>
              <div className="article__meta">
                <span>{caseStudy.role}</span>
                <span>{caseStudy.sector}</span>
                <span>{project.tech}</span>
              </div>

              <div className="article__content">
                <p className="case-study__lede">{caseStudy.overview}</p>

                <section className="case-study__block">
                  <h2>The problem</h2>
                  <ul>
                    {caseStudy.problem.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="case-study__block">
                  <h2>What I did</h2>
                  <ul>
                    {caseStudy.approach.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>

                <section className="case-study__block">
                  <h2>Measurable outcomes</h2>
                  <ul className="case-study__outcomes">
                    {caseStudy.outcomes.map((item, index) => (
                      <li key={index}>{item}</li>
                    ))}
                  </ul>
                </section>

                {project.link && (
                  <p>
                    <a href={project.link} target="_blank" rel="noopener noreferrer">
                      Visit {project.title} →
                    </a>
                  </p>
                )}
              </div>

              {project.slug !== 'amara-development' && (
                <div className="consult-cta case-study__cta">
                  <div>
                    <h2 className="consult-cta__title">Interested in similar work?</h2>
                    <p className="consult-cta__desc">
                      Get in touch about research collaboration, applied AI projects,
                      or related questions.
                    </p>
                  </div>
                  <a
                    className="btn btn--primary"
                    href={getSectionUrl('contact')}
                    onClick={(event) => {
                      event.preventDefault();
                      navigate('contact');
                    }}
                  >
                    Get in touch
                  </a>
                </div>
              )}
            </article>
          </div>
        </section>
      );
    }

    return (
      <section className="hero hero--compact">
        <div className="container">
          <p className="eyebrow">Portfolio</p>
          <h1 className="statement">Portfolio</h1>

          <div className="project-list">
            {WORK_HIGHLIGHT_PROJECTS.map((project, index) => {
              const category = project.tech.split(',')[0].trim();
              const status = project.status
                || (project.link ? 'Live product' : project.caseStudy ? 'Research demonstrator' : 'Prototype');
              const challenge = project.caseStudy?.overview || project.description;
              const approach = project.caseStudy?.approach?.join(' ') || null;
              const outcomes = project.caseStudy?.outcomes?.join(' ') || null;
              const learning = project.caseStudy?.excerpt || null;

              return (
                <article key={project.slug || project.title || index} className="project-entry">
                  <div className="project-entry__top">
                    <span className="project-entry__tag">{category}</span>
                    {project.caseStudy ? (
                      <a
                        className="project-entry__link"
                        href={getCaseStudyUrl(project.slug)}
                        onClick={(event) => {
                          event.preventDefault();
                          openCaseStudy(project.slug);
                        }}
                      >
                        Case study
                      </a>
                    ) : null}
                    {project.link ? (
                      <a
                        className="project-entry__link"
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Website
                      </a>
                    ) : null}
                  </div>

                  <h2 className="project-entry__title">{project.title}</h2>

                  <div className="project-entry__status">
                    <span className="project-entry__status-label">Status</span>
                    <span className="project-entry__status-value">{status}</span>
                    {project.metrics ? (
                      <span className="project-entry__status-meta">{project.metrics}</span>
                    ) : null}
                  </div>

                  <div className="project-entry__grid">
                    <div>
                      <h3 className="project-entry__label">Challenge</h3>
                      <p className="project-entry__text">{challenge}</p>
                    </div>
                    {approach ? (
                      <div>
                        <h3 className="project-entry__label">Approach</h3>
                        <p className="project-entry__text">{approach}</p>
                      </div>
                    ) : (
                      <div>
                        <h3 className="project-entry__label">Focus</h3>
                        <p className="project-entry__text">{project.tech}</p>
                      </div>
                    )}
                    {outcomes ? (
                      <div>
                        <h3 className="project-entry__label">Now</h3>
                        <p className="project-entry__text">{outcomes}</p>
                      </div>
                    ) : null}
                    {project.caseStudy?.role ? (
                      <div>
                        <h3 className="project-entry__label">Role</h3>
                        <p className="project-entry__text">
                          {project.caseStudy.role}
                          {project.caseStudy.sector ? ` · ${project.caseStudy.sector}` : ''}
                        </p>
                      </div>
                    ) : null}
                  </div>

                </article>
              );
            })}
          </div>
        </div>
      </section>
    );
  };

  const renderInsightsFollow = () => (
    <div className="consult-cta insights-follow">
      <div>
        <h2 className="consult-cta__title">Follow the insights</h2>
        <p className="consult-cta__desc">
          New writing on AI, research, and building across the UK and Saudi Arabia.
        </p>
      </div>
      <a
        className="btn btn--primary"
        href={NEWSLETTER_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => {
          window.gtag?.('event', 'generate_lead', {
            form_name: 'newsletter_follow',
            page_location: window.location.href
          });
        }}
      >
        {NEWSLETTER_CTA_LABEL}
      </a>
    </div>
  );

  const renderBlog = () => {
    const blogPosts = BLOG_POSTS;

    if (selectedArticle) {
      const article = blogPosts.find(post => post.id === selectedArticle);
      return (
        <section className="hero hero--compact page-enter" key={`article-${article.id}`}>
          <div className="container">
            <article className="article">
              <a
                className="back-link"
                href={getSectionUrl('blog')}
                onClick={(event) => {
                  event.preventDefault();
                  closeArticle();
                }}
              >
                ← Back to articles
              </a>
              <img
                className="article__hero"
                src={article.image}
                alt={`${article.title} — ${article.category}`}
                fetchPriority="high"
              />
              <h1 className="article__title">{article.title}</h1>
              <div className="article__meta">
                <time dateTime={article.dateISO}>{article.date}</time>
                <span>Manal Adham</span>
                <span>{article.readTime}</span>
                <span>{article.category}</span>
              </div>
              <div className="article__content">{article.content}</div>
              {renderInsightsFollow()}
            </article>
          </div>
        </section>
      );
    }

    return (
      <section className="hero hero--compact page-enter" key="blog-list">
        <div className="container">
          <p className="eyebrow">Blog</p>
          <h1 className="statement">Blog</h1>

          <div className="blog-list" style={{ marginTop: '64px' }}>
            {blogPosts.map((post) => (
              <article key={post.id} className="blog-entry">
                <div className="blog-entry__tags">
                  <span className="blog-entry__tag">{post.category}</span>
                </div>
                <h2 className="blog-entry__title">
                  <a
                    href={getArticleUrl(post.slug)}
                    onClick={(event) => {
                      event.preventDefault();
                      openArticle(post.id);
                    }}
                  >
                    {post.title}
                  </a>
                </h2>
                <p className="blog-entry__excerpt">{post.excerpt}</p>
                <div className="blog-entry__meta">
                  <time dateTime={post.dateISO}>{post.date}</time>
                  <span>{post.readTime}</span>
                  <a
                    className="blog-entry__read"
                    href={getArticleUrl(post.slug)}
                    onClick={(event) => {
                      event.preventDefault();
                      openArticle(post.id);
                    }}
                  >
                    Read →
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>
    );
  };

  const renderTalks = () => (
    <section className="hero hero--compact">
      <div className="container">
        <p className="eyebrow">Speaking</p>
        <h1 className="statement">
          Speaking
        </h1>
        <p className="lede" style={{ marginTop: '24px', maxWidth: '62ch' }}>
          Over the past decade, I have spoken at research conferences, founder forums,
          and industry events across the UK, Europe, Central Asia, and beyond, including
          IEEE CEC, GECCO, PPSN, the Female Founders Forum, and Saudi Global Network
          programmes.
        </p>

        <div style={{ marginTop: '64px' }}>
          <h2 className="eyebrow">Keynotes</h2>
          {keynotes.map((talk, index) => (
            <div key={index} className={`talk-feature${talk.image ? ' talk-feature--with-photo' : ''}`}>
              <div className="talk-feature__body">
                <div className="talk-feature__meta">
                  <span className="tag">{talk.role}</span>
                  <span className="talk-feature__date">{talk.date}</span>
                </div>
                <h3 className="talk-feature__title">{talk.title}</h3>
                <p className="talk-feature__event">{talk.event}</p>
                <p className="talk-feature__location">{talk.location}</p>
                <p className="talk-feature__desc">{talk.description}</p>
              </div>
              {talk.image && (
                <img
                  className="talk-feature__photo"
                  src={talk.image}
                  alt={talk.imageAlt || `${talk.title} — ${talk.event}`}
                  loading="lazy"
                />
              )}
            </div>
          ))}
        </div>

        <div style={{ marginTop: '64px', paddingTop: '48px', borderTop: '1px solid var(--line)' }}>
          <h2 className="eyebrow">Panels</h2>
          <div className="fireside-list">
            {firesideChats.map((chat, index) => (
              <div key={index} className="fireside-card">
                <span className="talk-feature__date">{chat.date}</span>
                <h3 className="fireside-card__title">{chat.title}</h3>
                <p className="fireside-card__guest">With {chat.guest}</p>
                <p className="fireside-card__event">{chat.event} • {chat.location}</p>
                <p className="fireside-card__desc">{chat.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '64px', paddingTop: '48px', borderTop: '1px solid var(--line)' }}>
          <h2 className="eyebrow">Presentations</h2>
          <div className="pub-list">
            {conferences.map((conf, index) => (
              <div key={index} className="pub-row" style={{ gridTemplateColumns: '1fr auto' }}>
                <div>
                  <h3 className="pub-row__title">{conf.name}</h3>
                  <p className="pub-row__meta">{conf.location} • {conf.role}</p>
                </div>
                <div className="pub-row__year">{conf.year}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );

  const renderConferences = () => (
    <section className="hero hero--compact">
      <div className="container">
        <p className="eyebrow">Publications</p>
        <h1 className="statement">
          Publications
        </h1>

        <div style={{ marginTop: '64px' }}>
          <div className="pub-list">
            {PUBLICATIONS.map((pub, index) => (
              <a
                key={index}
                className="pub-row pub-row--link"
                href={pub.link}
                target="_blank"
                rel="noopener noreferrer"
              >
                <PubLogo pub={pub} index={index} />
                <div>
                  <h2 className="pub-row__title">{pub.title}</h2>
                  <p className="pub-row__meta">{pub.venue}{pub.pages && ` • pp. ${pub.pages}`}</p>
                </div>
                <div className="pub-row__year">{pub.year}</div>
              </a>
            ))}
          </div>
        </div>

      </div>
    </section>
  );

  const renderConsulting = () => (
    <section className="hero hero--compact">
      <div className="container">
        <p className="eyebrow">Collaboration</p>
        <h1 className="statement">
          Collaboration
        </h1>
        <p className="lede" style={{ marginTop: '24px' }}>
          Ways to work together on applied AI, research partnerships, technical review,
          and speaking — grounded in research experience.
        </p>

        <div style={{ marginTop: '64px' }}>
          <h2 className="eyebrow">Areas of work</h2>
          <div className="offer-grid">
            {consultingOffers.map((offer, index) => (
              <div key={index} className="offer-card">
                <span className="offer-card__number">{String(index + 1).padStart(2, '0')}</span>
                <h3 className="offer-card__title">{offer.title}</h3>
                <p className="offer-card__desc">{offer.description}</p>
                <ul className="offer-card__list">
                  {offer.outcomes.map((outcome, i) => (
                    <li key={i}>{outcome}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div style={{ marginTop: '96px' }}>
          <h2 className="eyebrow">Process</h2>
          <div className="step-grid">
            {consultingSteps.map((step, index) => (
              <div key={index} className="step-card">
                <span className="step-card__number">{index + 1}</span>
                <h3 className="step-card__title">{step.title}</h3>
                <p className="step-card__desc">{step.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="consult-cta" id="book-intro">
          <div>
            <h2 className="consult-cta__title">Get in touch</h2>
            <p className="consult-cta__desc">
              For collaboration, research partnerships, or related questions,
              send a short note with context and what you are exploring.
            </p>
          </div>
          <div className="consult-cta__actions">
            <a
              className="btn btn--primary"
              href={getSectionUrl('contact')}
              onClick={(event) => {
                event.preventDefault();
                navigate('contact');
              }}
            >
              Contact
            </a>
          </div>
        </div>
      </div>
    </section>
  );

  const renderContact = () => (
    <section className="hero hero--compact">
      <div className="container">
        <p className="eyebrow">Contact</p>
        <h1 className="statement">
          Contact
        </h1>
        <div className="contact-intro" style={{ marginTop: '40px' }}>
          <p className="lede" style={{ maxWidth: '60ch' }}>
            Open to research collaborations, speaking, and joint exploration in AI
            and machine learning.
          </p>
          <div className="contact-links">
            <a
              href="https://www.linkedin.com/in/manaladham"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Manal Adham on LinkedIn"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
              </svg>
            </a>
            <a
              href="https://github.com/mta-adham"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Manal Adham on GitHub"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.03a9.55 9.55 0 0 1 5 0c1.91-1.3 2.75-1.03 2.75-1.03.55 1.37.2 2.39.1 2.64.64.7 1.03 1.6 1.03 2.69 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />
              </svg>
            </a>
            <a
              href={NEWSLETTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Manal Adham on Substack"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ padding: '3px' }}>
                <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
              </svg>
            </a>
          </div>
        </div>

        <div className="contact" style={{ marginTop: '48px' }}>
          <form className="contact__form contact__form--card" onSubmit={handleContactSubmit}>
            <label htmlFor="contact-name">Name</label>
            <input
              id="contact-name"
              type="text"
              name="name"
              value={contactForm.name}
              onChange={handleContactChange}
              required
              autoComplete="name"
              placeholder="Your name"
            />

            <label htmlFor="contact-email">Email address</label>
            <input
              id="contact-email"
              type="email"
              name="email"
              value={contactForm.email}
              onChange={handleContactChange}
              required
              autoComplete="email"
              placeholder="your@email.com"
            />

            <label htmlFor="contact-intent">What is this about?</label>
            <select
              id="contact-intent"
              name="intent"
              value={contactForm.intent}
              onChange={handleContactChange}
              required
            >
              <option value="" disabled>
                Select one
              </option>
              <option value="Research collaboration">Research collaboration</option>
              <option value="Speaking">Speaking</option>
              <option value="Portfolio">Portfolio</option>
              <option value="Other">Other</option>
            </select>

            <label htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              name="message"
              value={contactForm.message}
              onChange={handleContactChange}
              required
              rows={6}
              placeholder="Tell me about your project, event, or collaboration idea..."
            />

            <button
              type="submit"
              className="btn btn--primary"
              disabled={formStatus === 'sending'}
              style={{ opacity: formStatus === 'sending' ? 0.6 : 1 }}
            >
              {formStatus === 'sending' ? 'Sending…' : 'Send message'}
            </button>

            {formStatus === 'success' && (
              <div className="form-status form-status--success">
                Message sent successfully. I'll get back to you soon.
              </div>
            )}

            {formStatus === 'error' && (
              <div className="form-status form-status--error">
                Something went wrong. Please try again.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'home': return renderHome();
      case 'about': return renderAbout();
      case 'consulting': return renderConsulting();
      case 'projects': return renderProjects();
      case 'blog': return renderBlog();
      case 'talks': return renderTalks();
      case 'conferences': return renderConferences();
      case 'contact': return renderContact();
      default: return renderHome();
    }
  };

  return (
    <div>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <header className="site-header">
        <div className="site-header__inner">
          <a
            className="site-header__logo"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('home');
            }}
          >
            <span className="site-header__logo-mark" aria-hidden="true">M</span>
            <span>Adham</span>
          </a>
          <nav className="site-nav site-nav--desktop" aria-label="Primary navigation">
            {navItems.map(item => (
              <a
                key={item.id}
                href={getSectionUrl(item.id)}
                className={`site-nav__link ${activeSection === item.id ? 'site-nav__link--active' : ''}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(item.id);
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
          <button
            type="button"
            className="site-header__menu"
            aria-label={mobileNavOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={mobileNavOpen}
            onClick={() => setMobileNavOpen(open => !open)}
          >
            {mobileNavOpen ? 'Close' : 'Menu'}
          </button>
        </div>
        {mobileNavOpen && (
          <nav className="site-nav site-nav--mobile" aria-label="Mobile navigation">
            {navItems.map(item => (
              <a
                key={item.id}
                href={getSectionUrl(item.id)}
                className={`site-nav__link ${activeSection === item.id ? 'site-nav__link--active' : ''}`}
                aria-current={activeSection === item.id ? 'page' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  navigate(item.id);
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>
        )}
      </header>

      <main id="main-content">
        {renderContent()}
      </main>

      <footer className="site-footer">
        <div className="container">
          <div className="site-footer__grid">
            <div>
              <p className="site-footer__name">Manal Adham</p>
              <p style={{ margin: 0, maxWidth: '38ch' }}>
                AI research engineer. UCL PhD. Optimisation, machine learning, and AI.
              </p>
            </div>
            <nav className="site-footer__nav" aria-label="Footer navigation">
              <p className="site-footer__label">Explore</p>
              {navItems.map(item => (
                <a
                  key={item.id}
                  className="site-footer__text-link"
                  href={getSectionUrl(item.id)}
                  onClick={(event) => {
                    event.preventDefault();
                    navigate(item.id);
                  }}
                >
                  {item.label}
                </a>
              ))}
            </nav>
            <div className="site-footer__socials">
              <div className="site-footer__group">
                <p className="site-footer__label">Manal Adham</p>
                <div className="site-footer__items">
                  <a
                    className="site-footer__link"
                    href="https://www.linkedin.com/in/manaladham"
                    target="_blank"
                    rel="me noopener noreferrer"
                    aria-label="Manal Adham on LinkedIn"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href="https://github.com/mta-adham"
                    target="_blank"
                    rel="me noopener noreferrer"
                    aria-label="Manal Adham on GitHub"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M12 2a10 10 0 0 0-3.16 19.49c.5.09.68-.22.68-.48v-1.7c-2.78.6-3.37-1.34-3.37-1.34-.45-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.9 1.53 2.34 1.09 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.56-1.11-4.56-4.94 0-1.09.39-1.99 1.03-2.69-.1-.25-.45-1.27.1-2.64 0 0 .84-.27 2.75 1.03a9.55 9.55 0 0 1 5 0c1.91-1.3 2.75-1.03 2.75-1.03.55 1.37.2 2.39.1 2.64.64.7 1.03 1.6 1.03 2.69 0 3.84-2.34 4.68-4.57 4.93.36.31.68.92.68 1.85v2.74c0 .27.18.58.69.48A10 10 0 0 0 12 2z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href={NEWSLETTER_URL}
                    target="_blank"
                    rel="me noopener noreferrer"
                    aria-label="Manal Adham on Substack"
                  >
                    <svg
                      className="site-footer__icon site-footer__icon--sm"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
                    </svg>
                  </a>
                </div>
              </div>

              <div className="site-footer__group">
                <p className="site-footer__label">Saudi Global Network</p>
                <div className="site-footer__items">
                  <a
                    className="site-footer__link"
                    href="https://saudiglobalnetwork.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Saudi Global Network website"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M3 12h18" />
                      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href="https://www.linkedin.com/company/saudiglobalnetwork"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Saudi Global Network on LinkedIn"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M20.45 20.45h-3.55v-5.57c0-1.33-.03-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.36-1.85 3.6 0 4.27 2.37 4.27 5.45v6.29zM5.34 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12zM7.12 20.45H3.56V9h3.56v11.45z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href="https://www.instagram.com/saudiglobalnetwork"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Saudi Global Network on Instagram"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M7.8 2h8.4A5.8 5.8 0 0 1 22 7.8v8.4a5.8 5.8 0 0 1-5.8 5.8H7.8A5.8 5.8 0 0 1 2 16.2V7.8A5.8 5.8 0 0 1 7.8 2zm-.2 2A3.6 3.6 0 0 0 4 7.6v8.8A3.6 3.6 0 0 0 7.6 20h8.8a3.6 3.6 0 0 0 3.6-3.6V7.6A3.6 3.6 0 0 0 16.4 4H7.6zm9.65 1.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zM12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10zm0 2a3 3 0 1 0 0 6 3 3 0 0 0 0-6z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href="https://x.com/saudi_gnetwork"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Saudi Global Network on X"
                  >
                    <svg
                      className="site-footer__icon"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M18.24 2H21.5l-7.43 8.49L22.5 22h-6.59l-5.16-6.74L5.2 22H1.92l7.95-9.08L1.5 2h6.75l4.66 6.18L18.24 2zm-1.16 18h1.82L7.02 3.94H5.08L17.08 20z" />
                    </svg>
                  </a>
                  <a
                    className="site-footer__link"
                    href={SGN_SUBSTACK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Saudi Global Network on Substack"
                  >
                    <svg
                      className="site-footer__icon site-footer__icon--sm"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path d="M22.539 8.242H1.46V5.406h21.08v2.836zM1.46 10.812V24L12 18.11 22.54 24V10.812H1.46zM22.54 0H1.46v2.836h21.08V0z" />
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="site-footer__bottom">
            <p className="site-footer__copy">© 2026 Manal Adham. Innovation and AI Leader.</p>
            <button
              type="button"
              className="site-footer__settings"
              onClick={() => setAnalyticsConsent('unset')}
            >
              Analytics settings
            </button>
          </div>
        </div>
      </footer>

      {analyticsConsent === 'unset' && (
        <aside
          className="analytics-consent"
          role="region"
          aria-labelledby="analytics-consent-title"
        >
          <div>
            <h2 id="analytics-consent-title" className="analytics-consent__title">
              Help improve this website
            </h2>
            <p className="analytics-consent__text">
              Google Analytics helps me understand which pages are useful. It only loads
              if you choose to accept analytics.
            </p>
          </div>
          <div className="analytics-consent__actions">
            <button
              type="button"
              className="btn btn--secondary"
              onClick={() => updateAnalyticsConsent('denied')}
            >
              Decline
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => updateAnalyticsConsent('granted')}
            >
              Accept analytics
            </button>
          </div>
        </aside>
      )}
    </div>
  );
};

export default App;
